import { Box, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createProject, deleteProject, editProject, fetchProjects, refreshProjectAccessToken } from "../../api/projects";
import ProjectPopup from "./components/ProjectPopup";
import { deleteProjects, setProjects } from "../../store/slices/projectSlice";
import ConfirmationDialog from "../../components/common/confirmationDialog";
import { fetchAllSports } from "../../api/sports";

const createColumns = (handleOpenProjectPopup, handleDeleteProject, handleRefreshToken) => {
  return [
    {
      field: "id",
      headerName: "ID",
      minWidth: 50,
      flex: 0.5,
      sortable: false,
    },
    {
      field: "accessToken",
      headerName: "Access Token",
      minWidth: 300,
      flex: 2,
      sortable: false,
    },
    {
      field: "name",
      headerName: "Name",
      minWidth: 200,
      flex: 1.5,
      sortable: false,
    },
    {
      field: "timezoneOffset",
      headerName: "Timezone offset",
      minWidth: 150,
      flex: 1,
      sortable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 300,
      sortable: false,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            gap: "10px",
            padding: '5px',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenProjectPopup(row)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleRefreshToken(row)}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDeleteProject(row)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];
};

export default function Projects() {
  const projects = useSelector((state) => state.project.projects);
  const [isOpen, setIsOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [dialogData, setDialogData] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [sports, setSports] = useState(null);

  const dispatch = useDispatch();

  const handleEditProject = useCallback(async (project) => {
    const { id, accessToken, ...data } = project;
    return editProject(data, id);
  }, []);

  const handleCreateProject = useCallback((project) => {
    return createProject(project);
  }, []);

  const setupProjects = useCallback(async () => {
    const projects = await fetchProjects();
    dispatch(setProjects(projects || []));
  }, [dispatch]);

  const handleSubmit = useCallback(async (project) => {
    try {
      if (project.id) {
        await handleEditProject(project);
      } else {
        await handleCreateProject(project);
      }
      await setupProjects();
      setIsOpen(false);
      setActiveProject(null);
    } catch { }
  }, [setupProjects, handleCreateProject, handleEditProject]);

  const handleDeleteProject = useCallback((project) => {
    setDialogData({
      title: `Delete ${project.name}`,
      content: 'Are you sure you want to delete this project?',
      onConfirm: async () => {
        try {
          await deleteProject(project.id);
          setDialogData(null);
          dispatch(deleteProjects(project));
        } catch (e) {
          console.log(e)
        }
      },
      onClose: () => {
        setDialogData(null);
      },
    });
  }, [dispatch]);

  const handleRefreshToken = useCallback((project) => {
    setDialogData({
      title: `Refresh access token`,
      content: 'Are you sure you want to refresh this token? Other users will lose their access.',
      onConfirm: async () => {
        try {
          await refreshProjectAccessToken(project.id);
          await setupProjects();
          setDialogData(null);
        } catch (e) {
          console.log(e)
        }
      },
      onClose: () => {
        setDialogData(null);
      },
    });
  }, [setupProjects]);

  const handleOpenProjectPopup = useCallback((project) => {
    setActiveProject(project);
    setIsOpen(true);
  }, []);

  const columns = useMemo(() => {
    return createColumns(handleOpenProjectPopup, handleDeleteProject, handleRefreshToken);
  }, [handleOpenProjectPopup, handleDeleteProject, handleRefreshToken]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPage(0);
    setPageSize(newPageSize);
  }, []);

  const handleClose = useCallback(() => {
    setActiveProject(null);
    setIsOpen(false);
  }, []);

  const fetchSports = useCallback(async () => {
    const sports = await fetchAllSports();
    setSports(sports);
  }, []);

  useEffect(() => {
    setTotal((projects || []).length);
  }, [projects]);

  useEffect(() => {
    fetchSports();
  }, [fetchSports]);

  return (
    <div style={{ padding: '20px' }}>
      {dialogData && <ConfirmationDialog open={!!dialogData} title={dialogData.title} onClose={dialogData.onClose} onConfirm={dialogData.onConfirm} content={dialogData.content} />}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <h2>Projects</h2>
        <ProjectPopup isOpen={isOpen} project={activeProject} sports={sports} onClick={() => setIsOpen(true)} onClose={handleClose} onSave={handleSubmit} />
      </div>
      {projects && <Box sx={{ height: "700px", width: "100%" }}>
        <DataGrid
          getRowHeight={() => 'auto'}
          rows={projects}
          columns={columns}
          rowCount={total}
          pageSize={pageSize}
          page={page}
          paginationMode="server"
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          disableColumnFilter
          experimentalFeatures={{ newEditingApi: true }}
        />
      </Box>}
    </div>
  );
}
